import { 
  Box, 
  Button, 
  Card, 
  CircularProgress, 
  Typography, 
  Chip,
  Tooltip,
  Badge
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { uniqBy, groupBy } from "lodash";
import { useParams } from "react-router-dom";
import { memo, useCallback, useMemo, useState, useEffect } from "react";
import { httpsCallable } from "firebase/functions";
import { CloudFunctions, Firestore } from "../../../../../components/FirebaseAuth/firebase";
import { doc, getDoc } from "firebase/firestore";

const useStyles = makeStyles((theme) => ({
  extensionContainer: {
    padding: "16px",
    marginTop: "24px",
  },
  buttonContainer: {
    display: "flex",
    flexDirection: "row",
    gap: "1rem",
    marginBottom: "16px",
    [theme.breakpoints.down("md")]: {
      justifyContent: "space-between",
    },
  },
  summaryContainer: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "16px",
    padding: "12px",
    backgroundColor: "#f5f5f5",
    borderRadius: "8px",
  },
  categorySection: {
    marginBottom: "24px",
  },
  categoryHeader: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "12px",
    paddingBottom: "8px",
    borderBottom: "1px solid #e0e0e0",
  },
  techGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
    gap: "12px",
    padding: "8px 0",
  },
  techItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 12px",
    backgroundColor: "#ffffff",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    transition: "all 0.2s ease",
    "&:hover": {
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      transform: "translateY(-1px)",
      borderColor: "#1976d2",
    },
  },
  techIcon: {
    width: "24px",
    height: "24px",
    borderRadius: "4px",
    flexShrink: 0,
  },
  techName: {
    fontSize: "13px",
    fontWeight: 500,
    color: "#333",
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    lineHeight: "1.2",
  },
  categoryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
    gap: "8px",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
    },
  },
}));

// BuiltWith category mapping for cleaner display names
const getBuiltWithCategory = (tag, categories) => {
  // Use the first category if available, otherwise use the tag
  if (categories && categories.length > 0) {
    return categories[0];
  }
  
  // Fallback to tag-based categorization with better display names
  const tagMapping = {
    'cms': 'Content Management',
    'javascript-frameworks': 'JavaScript Frameworks',
    'analytics': 'Analytics & Tracking',
    'cdn': 'CDN & Performance',
    'ecommerce': 'E-commerce',
    'hosting': 'Hosting & Cloud',
    'security': 'Security',
    'marketing': 'Marketing Tools',
    'social': 'Social Media',
    'media': 'Media & Content',
    'database': 'Database',
    'web-server': 'Web Servers',
    'payment': 'Payment Processing',
    'email': 'Email Services',
    'monitoring': 'Monitoring & Analytics',
    'css-frameworks': 'CSS Frameworks',
    'javascript-libraries': 'JavaScript Libraries',
    'web-frameworks': 'Web Frameworks',
    'advertising': 'Advertising',
    'widgets': 'Widgets & Plugins'
  };
  
  return tagMapping[tag] || (tag ? tag.charAt(0).toUpperCase() + tag.slice(1).replace(/-/g, ' ') : 'Other');
};

const DiscoveredExtensions = memo(({ siteData, fetchSiteData }) => {
  const classes = useStyles();
  const params = useParams();
  const [loading, setLoading] = useState(false);
  const [builtWithData, setBuiltWithData] = useState(null);
  const [builtWithLoading, setBuiltWithLoading] = useState(false);


  const fetchBuiltWithData = useCallback(async () => {
    try {
      setBuiltWithLoading(true);
      const builtWithDocRef = doc(Firestore, `accounts/${params.accountId}/sites/${params.siteId}/builtwith_data/latest`);
      const builtWithDoc = await getDoc(builtWithDocRef);
      
      if (builtWithDoc.exists()) {
        setBuiltWithData(builtWithDoc.data());
      } else {
        setBuiltWithData(null);
      }
    } catch (error) {
      console.error("Error fetching BuiltWith data:", error);
      setBuiltWithData(null);
    } finally {
      setBuiltWithLoading(false);
    }
  }, [params.accountId, params.siteId]);

  const handleRecheck = useCallback(async () => {
    setLoading(true);
    // Trigger BuiltWith data update (prioritizes URL field if available)
    const updateBuiltWithData = httpsCallable(
      CloudFunctions,
      "kwallCloud-updateBuiltWithData"
    );
    
    // Determine environment based on site data - Manual sites always have URLs
    const env = (siteData?.siteType === "Manual" || (siteData?.url && siteData.url.trim())) ? "production" : "dev";
    
    await updateBuiltWithData({
      accountId: params.accountId,
      siteId: params.siteId,
      env: env,
    });
    
    // Refresh the data after update
    setTimeout(() => {
      fetchBuiltWithData();
    }, 2000); // Wait 2 seconds for the cloud function to complete
    
    setLoading(false);
  }, [params.accountId, params.siteId, fetchBuiltWithData, siteData?.url, siteData?.siteType]);

  // Fetch BuiltWith data on component mount
  useEffect(() => {
    fetchBuiltWithData();
  }, [fetchBuiltWithData]);


  const extensions = useMemo(() => {
    // If we have BuiltWith data, use it; otherwise fall back to legacy data
    if (builtWithData && builtWithData.technologies) {
      return builtWithData.technologies.map(tech => ({
        title: tech.name,
        name: tech.name,
        description: tech.description,
        link: tech.link,
        category: getBuiltWithCategory(tech.tag, tech.categories),
        tag: tech.tag,
        categories: tech.categories,
        isPremium: tech.isPremium,
        firstDetected: tech.firstDetected,
        lastDetected: tech.lastDetected,
        isLive: tech.isLive,
        image: `https://www.google.com/s2/favicons?sz=64&domain_url=${tech.link || tech.name}`,
      }));
    }
    
    // Fallback to legacy web check data
    const prevExtensions = siteData && siteData.prevExtensions ? siteData.prevExtensions : [];
    const stackPacks =
      siteData?.["web_check_result_dev"]?.stackPacks ||
      siteData?.["web_check_result_external"]?.stackPacks ||
      [];
    const entities =
      siteData?.["web_check_result_dev"]?.entities ||
      siteData?.["web_check_result_external"]?.entities ||
      [];
    const convertedExtensions =
      prevExtensions.length > 0 ? prevExtensions : [...stackPacks, ...entities];
    return uniqBy(convertedExtensions, "title");
  }, [siteData, builtWithData]);
  
  const categorizedExtensions = useMemo(() => {
    const categorized = groupBy(extensions, (ext) => ext.category || 'Other');
    // Sort categories alphabetically, but put "Other" at the end
    const sortedCategories = Object.keys(categorized).sort((a, b) => {
      if (a === 'Other') return 1;
      if (b === 'Other') return -1;
      return a.localeCompare(b);
    });
    
    const result = {};
    sortedCategories.forEach(category => {
      result[category] = categorized[category];
    });
    
    return result;
  }, [extensions]);

  const totalTechnologies = extensions.length;
  const categoryCount = Object.keys(categorizedExtensions).length;

  const renderCategorizedView = () => (
    <Box>
      {Object.entries(categorizedExtensions).map(([category, categoryExtensions]) => (
        <Box key={category} className={classes.categorySection}>
          <Box className={classes.categoryHeader}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#333" }}>
              {category}
            </Typography>
            <Badge badgeContent={categoryExtensions.length} color="primary" size="small" />
          </Box>
          <Box className={classes.categoryGrid}>
            {categoryExtensions.map((extension, index) => (
              <Tooltip key={index} title={extension.description || extension.title}>
                <Box className={classes.techItem}>
                  <img
                    src={extension.image}
                    alt={extension.title}
                    className={classes.techIcon}
                  />
                  <Typography className={classes.techName}>
                    {extension.title}
                  </Typography>
                </Box>
              </Tooltip>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );

  return (
    <Card className={classes.extensionContainer}>
      {/* Summary Header */}
      <Box className={classes.summaryContainer}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Tech Stack Discovery
        </Typography>
        <Chip 
          label={`${totalTechnologies} technologies`} 
          color="primary" 
          size="small" 
        />
        <Chip 
          label={`${categoryCount} categories`} 
          color="secondary" 
          size="small" 
        />
      </Box>

      {/* Action Buttons */}
      <Box className={classes.buttonContainer}>
        {loading ? (
          <CircularProgress
            variant="indeterminate"
            sx={{ color: "#9F9F9F" }}
            size={24}
          />
        ) : (
          <Button variant="contained" onClick={handleRecheck}>
            Refresh Tech Stack
          </Button>
        )}
      </Box>

      {/* Content Display */}
      {builtWithLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress sx={{ color: "#9F9F9F" }} size={32} />
        </Box>
      ) : totalTechnologies === 0 ? (
        <Box sx={{ textAlign: 'center', p: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No technologies detected yet. Click "Refresh Tech Stack" to scan.
          </Typography>
        </Box>
      ) : (
        renderCategorizedView()
      )}

    </Card>
  );
});

export { DiscoveredExtensions };
